import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ShareIcon from '../assets/images/share-click.JPG';
import AddToHome from '../assets/images/add-to-home.JPG';
import AddTlkButton from '../assets/images/add-button-tlk.JPG';
// Place the tutorial clip at src/assets/videos/tlk-add-to-home-safari.mp4
import AddToHomeVideoIOS from '../assets/videos/tlk-add-to-home-safari.mp4';

/* ---------- Fallback mockups for platforms without a real screenshot ---------- *
 * Android currently has no captured screenshots, so it keeps the lightweight     *
 * illustrative SVG mockups. iOS now uses the real imported screenshots below.    */

const ANDROID_MENU_VISUAL = (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
        <rect x="1" y="1" width="62" height="62" rx="12" fill="#f4f2f5" stroke="#e7e1e8" />
        <rect x="5" y="6" width="54" height="12" rx="6" fill="#ffffff" stroke="#e2dbe3" />
        <circle cx="47" cy="12" r="1.6" fill="#8a6478" />
        <circle cx="52" cy="12" r="1.6" fill="#8a6478" />
        <circle cx="42" cy="12" r="1.6" fill="#8a6478" />
        <circle cx="47" cy="12" r="9" fill="#FF4DA6" opacity="0.16" />
    </svg>
);

const ANDROID_ADD_HOME_VISUAL = (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
        <rect x="1" y="1" width="62" height="62" rx="12" fill="#2c222b" />
        <rect x="7" y="8" width="50" height="10" rx="4" fill="#3a2f39" />
        <rect x="12" y="11.7" width="24" height="2.6" rx="1.3" fill="#8a7286" />
        <rect x="7" y="21" width="50" height="11" rx="4" fill="#FF4DA6" opacity="0.9" />
        <rect x="12" y="25" width="30" height="2.8" rx="1.4" fill="#fff" />
        <rect x="7" y="35" width="50" height="9" rx="4" fill="#3a2f39" />
        <rect x="12" y="38.3" width="18" height="2.5" rx="1.25" fill="#8a7286" />
    </svg>
);

const ANDROID_INSTALL_VISUAL = (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
        <rect x="1" y="1" width="62" height="62" rx="12" fill="#2c222b" />
        <circle cx="32" cy="24" r="10" fill="url(#g2)" />
        <defs>
            <linearGradient id="g2" x1="22" y1="14" x2="42" y2="34" gradientUnits="userSpaceOnUse">
                <stop stopColor="#AF0056" />
                <stop offset="1" stopColor="#FF4DA6" />
            </linearGradient>
        </defs>
        <rect x="14" y="42" width="36" height="12" rx="6" fill="#FF4DA6" />
        <rect x="22" y="46.5" width="20" height="3" rx="1.5" fill="#fff" />
    </svg>
);

const IOS_STEPS = [
    {
        number: '1',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
        ),
        title: 'Tap Share',
        desc: 'Tap the Share button at the bottom of Safari',
        image: ShareIcon,
    },
    {
        number: '2',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
        ),
        title: 'Add to Home Screen',
        desc: 'Scroll down and tap "Add to Home Screen"',
        image: AddToHome,
    },
    {
        number: '3',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        title: 'Tap Add',
        desc: 'Tap "Add" in the top-right corner to install',
        image: AddTlkButton,
    },
];

const ANDROID_STEPS = [
    {
        number: '1',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
            </svg>
        ),
        title: 'Open browser menu',
        desc: 'Tap the three-dot menu at the top-right of Chrome',
        visual: ANDROID_MENU_VISUAL,
    },
    {
        number: '2',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
        title: 'Tap "Add to Home screen"',
        desc: 'Select "Install app" or "Add to Home screen"',
        visual: ANDROID_ADD_HOME_VISUAL,
    },
    {
        number: '3',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        title: 'Tap Install',
        desc: 'Confirm by tapping "Install" in the prompt',
        visual: ANDROID_INSTALL_VISUAL,
    },
];

const StepList = ({ steps }) => (
    <div className="pwa-step-list">
        {steps.map((step, i) => (
            <motion.div
                key={step.number}
                className="pwa-step"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08, type: 'spring', stiffness: 300, damping: 26 }}
            >
                <div className="pwa-step-row">
                    <div className="pwa-step-number">{step.number}</div>

                    <div className="pwa-step-body">
                        <div className="pwa-step-head">
                            <span className="pwa-step-icon">{step.icon}</span>
                            <span className="pwa-step-title">{step.title}</span>
                        </div>
                        <span className="pwa-step-desc">{step.desc}</span>
                    </div>
                </div>

                {/* Big screenshot */}
                {/* {step.image ? (
                    <div style={{
                        marginTop: 10,
                        borderRadius: 14,
                        overflow: 'hidden',
                        border: '1px solid #ffd5e7',
                        background: '#f4f2f5',
                    }}>
                        <img
                            src={step.image}
                            alt={step.title}
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: 260,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    </div>
                ) : step.visual ? (
                    <div style={{
                        marginTop: 10,
                        borderRadius: 14,
                        overflow: 'hidden',
                        border: '1px solid #ffd5e7',
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        <div style={{ width: 220, height: 220 }}>
                            {React.cloneElement(step.visual, { width: '100%', height: '100%' })}
                        </div>
                    </div>
                ) : null} */}
            </motion.div>
        ))}
    </div>
);

/* ---------- iOS/Safari tutorial video (replaces the step list on the iOS tab) ---------- */

const IOSVideoCard = () => {
    const videoRef = React.useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        v.play();
        setIsPlaying(true);
    };

    return (
        <div className="pwa-video-card">
            <video
                ref={videoRef}
                src={AddToHomeVideoIOS}
                playsInline
                controls={isPlaying}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                }}
            />

            {!isPlaying && (
                <button className="pwa-video-play" onClick={handlePlay} aria-label="Play tutorial video">
                    <div className="pwa-video-play-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                            <polygon points="8,5 20,12 8,19" />
                        </svg>
                    </div>
                </button>
            )}
        </div>
    );
};

const PwaInstallTip = ({ open, onClose, onInstalled, platform = 'other' }) => {
    const isIos = platform === 'ios';
    const isAndroid = platform === 'android';
    // if unknown, show both with a toggle
    const [activeTab, setActiveTab] = useState(isAndroid ? 'android' : 'ios');

    const steps = activeTab === 'ios' ? IOS_STEPS : ANDROID_STEPS;
    const showTabs = !isIos && !isAndroid; // only show tabs when platform is unknown

    const dismiss = () => {
        onClose?.();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="pwa-install-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        role="dialog"
                        aria-labelledby="pwa-tip-title"
                        className="pwa-install-modal"
                        initial={{ y: 28, scale: 0.95, opacity: 0 }}
                        animate={{ y: 0, scale: 1, opacity: 1 }}
                        exit={{ y: 20, scale: 0.96, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="pwa-install-glow" />

                        <div className="pwa-install-content">
                            {onClose && (
                                <button className="pwa-install-close" aria-label="Close" onClick={dismiss}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                                    </svg>
                                </button>
                            )}

                            {/* Header */}
                            <div className="pwa-install-badge">Install TLK</div>
                            <h2 id="pwa-tip-title" className="pwa-install-heading">
                                Install TLK in 10 seconds
                            </h2>
                            <p className="pwa-install-copy">
                                Launch TLK like a real app. Full-screen access, faster load, no {isIos ? 'App Store' : 'Play Store'} required.
                            </p>

                            {/* Platform tabs — only show when platform is unknown */}
                            {showTabs && (
                                <div className="pwa-install-tabs">
                                    {['ios', 'android'].map((tab) => (
                                        <button
                                            key={tab}
                                            className={`pwa-install-tab${activeTab === tab ? ' active' : ''}`}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab === 'ios' ? (
                                                <>
                                                    <svg className="pwa-install-tab-logo" viewBox="0 0 20 20" aria-hidden="true">
                                                        <path fill="currentColor" d="M17.7635 6.81868C17.6475 6.90869 15.5993 8.06281 15.5993 10.6291C15.5993 13.5974 18.2056 14.6475 18.2836 14.6735C18.2716 14.7375 17.8695 16.1116 16.9094 17.5118C16.0533 18.7439 15.1593 19.974 13.7991 19.974C12.439 19.974 12.089 19.1839 10.5188 19.1839C8.98869 19.1839 8.44464 20 7.20052 20C5.95639 20 5.0883 18.8599 4.0902 17.4597C2.93409 15.8156 2 13.2613 2 10.8371C2 6.9487 4.52825 4.88649 7.0165 4.88649C8.33863 4.88649 9.44074 5.75458 10.2708 5.75458C11.0608 5.75458 12.293 4.83448 13.7971 4.83448C14.3672 4.83448 16.4154 4.88649 17.7635 6.81868ZM13.0831 3.18832C13.7051 2.45025 14.1452 1.42614 14.1452 0.40204C14.1452 0.260026 14.1332 0.116012 14.1072 0C13.0951 0.0380038 11.8909 0.674068 11.1649 1.51615C10.5948 2.16422 10.0628 3.18832 10.0628 4.22642C10.0628 4.38244 10.0888 4.53845 10.1009 4.58846C10.1648 4.60046 10.2688 4.61446 10.3728 4.61446C11.2809 4.61446 12.423 4.0064 13.0831 3.18832Z" />
                                                    </svg>
                                                    iOS
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="pwa-install-tab-logo" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path fill="currentColor" d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.3-.15-.68-.04-.84.26l-1.87 3.23A11.4 11.4 0 0 0 12 8c-1.5 0-2.92.32-4.2.94L5.66 5.71c-.16-.3-.54-.41-.84-.26-.3.16-.42.54-.26.85l1.84 3.18C3.8 11.1 2 13.86 2 17h20c0-3.14-1.8-5.9-4.4-7.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
                                                    </svg>
                                                    Android
                                                </>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Steps / video */}
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.18 }}
                                >
                                    {activeTab === 'ios' ? (
                                        <IOSVideoCard />
                                    ) : (
                                        <StepList steps={steps} />
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            {/* Actions */}
                            <div className="pwa-install-actions">
                                <button
                                    className="pwa-btn-secondary"
                                    onClick={dismiss}
                                >
                                    Not now
                                </button>
                                <button
                                    className="pwa-btn-primary"
                                    onClick={() => (onInstalled || onClose)?.()}
                                >
                                    Install TLK
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PwaInstallTip;